import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { RsaEncryptService } from '@core/utils';
import { BehaviorSubject, Subject } from 'rxjs';
import { Price } from '../types/pricing.types';
import { SignUpSteps } from '../types/sign-up.types';
import { PricingFormService } from './pricing-form.service';
import { emailDomainValidator } from '../utils/emailValidatorStripe';
import { digitLength } from '../utils/digitsLenghtValidator';

@Injectable()
export class SignUpFormService
{
  private _signUpForm: FormGroup;
  private _paymentMethod = new BehaviorSubject<number>(1);

  steps = Object.keys(SignUpSteps)
    .map((key) => ({
      id: SignUpSteps[key],
      label: key,
    }))
    .filter((step) => typeof step.id === 'number');

  private _step = new BehaviorSubject<number>(1);
  private _stepBussiness = new BehaviorSubject<number>(0);

  public step$ = (this._step as Subject<number>).asObservable();
  public stepBussiness$ = (this._stepBussiness as Subject<number>).asObservable();


  private _formStatus = new BehaviorSubject<boolean>(true);
  public formStatus$ = (this._formStatus as Subject<boolean>).asObservable();

  constructor(
    private _formBuilder: FormBuilder,
    private _rsa: RsaEncryptService,
    private _router: Router,
    private _pricingFormService: PricingFormService
  )
  {
    this._buildForm();
  }

  set step(id: number)
  {
    const step = this.steps.find((item) => item.id === id);
    this._step.next(step.id);
    this._router.navigate(['/sign-up', step.label]);
  }

  get step()
  {
    return this._step.getValue();
  }

  set stepBussiness(id: number)
  {
    this._stepBussiness.next(id);
  }

  get stepBussiness()
  {
    return this._stepBussiness.getValue();
  }

  setStatusForm(value: boolean)
  {
    this._formStatus.next(value);
  }

  get hasNextStep()
  {
    return !!this.steps.find((stepItem) => stepItem.id === this.step + 1);
  }

  get hasPreviousStep()
  {
    return !!this.steps.find((stepItem) => stepItem.id === this.step - 1);
  }

  get paymentMethod()
  {
    return this._paymentMethod.getValue();
  }

  set paymentMethod(value: number)
  {
    this._paymentMethod.next(value);
  }

  nextStep()
  {
    const nextStep = this.steps.find((step) => step.id === this.step + 1);
    if (nextStep)
    {
      this._step.next(nextStep.id);
      if (nextStep.id == 2)
      {
        this._stepBussiness.next(0);
      }
      this._router.navigate(['/sign-up', nextStep.label]);
    }
  }
  changeStep(id)
  {
    const nextStep = this.steps.find((step) => step.id === id);
    this._step.next(nextStep?.id || 1);
  }

  previousStep()
  {
    if (this.step == 2 && this._stepBussiness.getValue() == 1)
    {
      this._stepBussiness.next(0);
      return;
    }
    const previousStep = this.steps.find((step) => step.id === this.step - 1);
    if (previousStep)
    {
      this._step.next(previousStep.id);
      if (previousStep.id == 2)
      {
        this._stepBussiness.next(1);
      }
      this._router.navigate(['/sign-up', previousStep.label]);
    }
  }

  beginningStep()
  {
    const step = this.steps[0];
    this._step.next(step.id);
    this._router.navigate(['/sign-up', step.label]);
  }

  get form()
  {
    return this._signUpForm;
  }

  get itemsListFA()
  {
    return this._signUpForm.get('ItemsList') as FormArray;
  }

  get personalDataFG()
  {
    return this._signUpForm.get('personalData') as FormGroup;
  }

  get businessDataFG()
  {
    return this._signUpForm.get('businessData') as FormGroup;
  }

  get checkoutFG()
  {
    return this._signUpForm.get('checkout') as FormGroup;
  }

  get formRawValue()
  {
    if (this.checkoutFG.get('isTarjeta').value)
    {
      this.paymentMethod = 1;
      return {
        ItemsList: this.itemsListFA.value,
        ...this.personalDataFG.value,
        ...this.businessDataFG.value,
        ...this.checkoutFG.value,
        claveUsuario: this._rsa.encryptWithPublicKey(
          this.personalDataFG.value.claveUsuario
        ),
        cardNumber: this._rsa.encryptWithPublicKey(
          this.checkoutFG.value.cardNumber
        ),
        cardCVV: this._rsa.encryptWithPublicKey(this.checkoutFG.value.cardCVV),
        cardMonth: this.checkoutFG.value.cardMonth.substring(0, 2),
        cardYear: this.checkoutFG.value.cardMonth.substring(3, 5),
        last4: this.checkoutFG.value.cardNumber.slice(-4),
        subtotal: +this._signUpForm.get('subtotal').value.toFixed(4),
        taxes: +this._signUpForm.get('taxes').value.toFixed(4),
        total: +this._signUpForm.get('total').value.toFixed(4),
      };
    } else
    {
      this.paymentMethod = 2;
      return {
        ItemsList: this.itemsListFA.value,
        ...this.personalDataFG.value,
        ...this.businessDataFG.value,
        ...this.checkoutFG.value,
        claveUsuario: this._rsa.encryptWithPublicKey(
          this.personalDataFG.value.claveUsuario
        ),
        subtotal: +this._signUpForm.get('subtotal').value.toFixed(4),
        taxes: +this._signUpForm.get('taxes').value.toFixed(4),
        total: +this._signUpForm.get('total').value.toFixed(4),
      };
    }
  }

  getControl(formControlName: string)
  {
    return this._signUpForm.get(formControlName);
  }

  setPricingPlan(plan: Price)
  {
    this.itemsListFA.clear();
    this.itemsListFA.insert(
      0,
      this._itemListFG({ ...plan, Quantity: 1, Checked: true })
    );
  }

  setItemsListFA(items: Price[])
  {
    const firstItem = this.itemsListFA.at(0);
    this.itemsListFA.clear();
    if (firstItem)
    {
      this.itemsListFA.push(firstItem);
      items.forEach((item) => this.itemsListFA.push(this._itemListFG(item)));
    }
  }

  removeAddon(item: Price)
  {
    const index = this.itemsListFA.controls.findIndex(
      (itemList) => itemList.value.ProductId === item.ProductId
    );
    this.itemsListFA.removeAt(index);

    const pricingIndex = this._pricingFormService.addonsFA.controls.findIndex(
      (itemList) => itemList.value.ProductId === item.ProductId
    );
    this._pricingFormService.addonsFA
      .at(pricingIndex)
      .get('Checked')
      .setValue(false);
    this._pricingFormService.addonsFA
      .at(pricingIndex)
      .get('Quantity')
      .setValue(1);
  }

  toogleAddon(price: Price)
  {
    const item = this.itemsListFA.controls.find(
      (itemFG) => itemFG.value.PriceId === price.PriceId
    );
    if (item)
    {
      item.get('Checked').setValue(!item.value.Checked);
      item.get('Quantity').setValue(1);
    }
  }

  private _buildForm()
  {
    this._signUpForm = this._formBuilder.group({
      ItemsList: this._formBuilder.array([]),
      personalData: this._formBuilder.group(
        {
          nombreUsuario: ['', [Validators.required]],
          correoUsuario: ['', [Validators.required, Validators.email, emailDomainValidator()]],
          claveUsuario: ['', [Validators.required]],
          telefonoContacto: ['', [Validators.required, Validators.minLength(8)]],
          paisUsuario: ['', [Validators.required]],
          codVerificacion: [''],
          isVerified: [false],
        }
      ),
      businessData: this._formBuilder.group({
        // first part
        nombreOrganizacion: ['', [Validators.required]],
        codTipoNegocio: [0, [Validators.required]],
        codCategoriaNegocio: [0, [Validators.required]],
        nuevaCategoria: [''],
        // second part
        numeroOrganizacion: ['', [Validators.required, Validators.minLength(8)]],
        razonSocial: ['', [Validators.required]],
        codTipoSociedad: ['', [Validators.required]],
        codPais: [0, [Validators.required]],
        codDepartamento: [0, [Validators.required]],
        codCiudad: [0, [Validators.required]],
        direccionOrganizacion: ['', [Validators.required]],
        direccionLatitud: [15.497566],
        direccionLongitud: [-88.036431],
        rtn: ['', []],
        codigoPostal: ['', [Validators.required, digitLength(5)]],
        DireccionFija: [true],
        actualsection: [0],
        logo: [''],
        regimenFiscal: [null],
        sameAddressOrganization: [false]
      }),
      checkout: this._formBuilder.group({
        isTarjeta: [true, [Validators.required]],
        cardName: ['', [Validators.required]],
        cardNumber: ['', [Validators.required]],
        cardMonth: ['', [Validators.required]],
        cardYear: [''],
        cardCVV: ['', [Validators.required]],
        //
        nombreFactura: ['', [Validators.required]],
        tipoDocumento: ['', [Validators.required]],
        rtnCO: ['', [Validators.required]],
        organizacion: [''],
        //
        codPaisCO: ['', [Validators.required]],
        codDepartamentoCO: ['', []],
        codCiudadCO: ['', []],
        codPostalCO: ['', []],
        direccionCO: ['', [Validators.required]],
        telefonoCO: ['', [Validators.required]],
        sameAddress: [false],
        //
        Banco: [''],
        NumReferencia: [''],
        FechaPago: [''],
        ImagenRecibo: [''],
        NombreOrganizacion: ['']

      }),
      subtotal: [0],
      total: [0],
      taxes: [0],
    });
  }

  resetForm()
  {
    this.step = SignUpSteps['personal-data'];
    this.setStatusForm(true);
    this._signUpForm.reset({ emitEvent: false });
    this._signUpForm.get('ItemsList').reset({ emitEvent: false });
    this._signUpForm.get('personalData').reset({ emitEvent: false });
    this._signUpForm.get('businessData').reset({
      emitEvent: false, direccionOrganizacion: ''
    });

    this._signUpForm.get('checkout').reset({ emitEvent: false });
    this.personalDataFG.get('isVerified').setValue(false, { emitEvent: false });
    this.businessDataFG
      .get('direccionLatitud')
      .setValue(15.505376717509272, { emitEvent: false });
    this.businessDataFG
      .get('direccionLongitud')
      .setValue(-88.02491452402643, { emitEvent: false });
    this.checkoutFG.get('isTarjeta').setValue(true, { emitEvent: false });
    this._signUpForm.get('subtotal').setValue(0, { emitEvent: false });
    this._signUpForm.get('taxes').setValue(0, { emitEvent: false });
    this._signUpForm.get('total').setValue(0, { emitEvent: false });
  }

  private _itemListFG(item: Price)
  {
    return this._formBuilder.group({
      Quantity: [item.Quantity || 1],
      Checked: [item.Checked || false],
      ProductId: [item.ProductId],
      Name: [item.Name],
      PriceId: [item.PriceId],
      Taxes: [item.Taxes / 100],
      Unit: [item.Unit / 100],
      UnitAmount: [item.UnitAmount / 100],
      Interval: [item.Interval],
      Metadata: [item.Metadata],
      Order: [item.Order],
    });
  }
}
